// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { configApi } from '@/api/config-api'
import type { Config } from '@/api/types/config'
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';

export const useConfig = () => {
  const queryClient = useQueryClient()
  // Use optional chaining and defaults to prevent errors if AuthContext isn't available
  const { selectedOrganizationId = null, selectedWorkspaceId = null } = useAuth() || {}

  const { data, isPending, isError } = useQuery({
    queryKey: ['config', selectedOrganizationId, selectedWorkspaceId],
    queryFn: async () => {
      console.log('[useConfig] Fetching workspace config');
      
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        console.log('[useConfig] No workspace selected, returning null');
        return null;
      }
      
      try {
        const config = await configApi.getWorkspaceConfig(
          selectedOrganizationId,
          selectedWorkspaceId
        )
        
        return config
      } catch (error) {
        console.error('[useConfig] Failed to load config:', error)
        throw error
      }
    },
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId
  })

  const mutation = useMutation({
    mutationFn: async (data: Partial<Config>) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('No workspace selected')
      }
      
      console.log('[useConfig] Updating workspace config with:', Object.keys(data));
      console.trace('[useConfig] Update call stack');
      return configApi.updateWorkspaceConfig(
        selectedOrganizationId,
        selectedWorkspaceId,
        data
      )
    },
    onSuccess: () => {
      // Invalidate both config and bills queries
      queryClient.invalidateQueries({ queryKey: ['config', selectedOrganizationId, selectedWorkspaceId] })
      queryClient.invalidateQueries({ queryKey: ['bills', null, null, selectedOrganizationId, selectedWorkspaceId] })
    },
    onError: (error) => {
      console.error('Failed to update config:', error)
      throw error
    }
  })

  return {
    config: data,
    isLoading: isPending,
    error: isError ? 'Failed to load configuration from database.' : null,
    updateConfig: mutation
  }
}
